import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { TipoEtapaKanbanEnum } from '@shared/enums/servico/tipo-etapa-kanban.enum';
import { OpcaoGatilhoAutomacaoOrdemServicoEnum } from '@shared/enums/servico/opcao-gatilho-automacao-ordem-servico.enum';
import { ModoAoVencerAutomacaoOrdemServicoEnum } from '@shared/enums/servico/modo-ao-vencer-automacao-ordem-servico.enum';
import { TipoDestinatarioAutomacaoOrdemServicoEnum } from '@shared/enums/servico/tipo-destinatario-automacao-ordem-servico.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { AutomacaoOrdemServicoService } from '@shared/services/apis/servico/automacao-ordem-servico.service';
import { ConfigKanbanService } from '@shared/services/apis/servico/config-kanban.service';
import { PerfilService } from '@shared/services/apis/usuario/perfil.service';
import { UsuarioService } from '@shared/services/apis/usuario/usuario.service';
import { AutomacaoOrdemServicoSalvarRequest } from '@shared/services/requests/servico/automacao-ordem-servico-salvar.request';
import { GetAutomacaoOrdemServicoResponse } from '@shared/services/responses/servico/get-automacao-ordem-servico.response';
import { AutomacaoOrdemServicoDestinatarioStruct } from '@shared/services/structs/servico/automacao-ordem-servico-destinatario.struct';
import { AutomacaoOrdemServicoOpcaoStruct } from '@shared/services/structs/servico/automacao-ordem-servico-opcao.struct';
import { EtapaKanbanStruct } from '@shared/services/structs/servico/etapa-kanban.struct';

interface Gatilho {
  idOpcao: number;
  idEtapaKanban: number | null;
}

interface OpcaoGatilho extends Gatilho {
  rotulo: string;
}

interface LinhaAutomacao {
  ativoCtrl: string;
  gatilhoCtrl: string;
  nome: string;
  icone: string;
  descricao: string;
  nota: string;
  isEtapaOferecida: (etapa: EtapaKanbanStruct) => boolean;
  opcoesCatalogo: (res: GetAutomacaoOrdemServicoResponse) => AutomacaoOrdemServicoOpcaoStruct[];
  opcoes: OpcaoGatilho[];
}

interface DestinatarioOpcao {
  valor: string;
  label: string;
}

const NOTA_INTERMEDIARIA = 'Oferece apenas etapas intermediárias: nem a inicial, nem as finais.';
const TOTAL_AUTOMACOES = 6;

@Component({
  selector: 'app-automacoes-ordem-servico',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    FooterActionComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './automacoes-ordem-servico.component.html',
  styleUrls: ['./automacoes-ordem-servico.component.css'],
})
export class AutomacoesOrdemServicoComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public modoAoVencerEnum = ModoAoVencerAutomacaoOrdemServicoEnum;

  public isLoading = false;
  public isFalhaConfig = false;
  public salvando = false;
  public tentouSalvar = false;
  public mostrarComoFunciona = false;

  public model: FormGroup;
  public integerMask: object;
  public opcoesAoVencer: AutomacaoOrdemServicoOpcaoStruct[] = [];

  public destinatariosDoCard: DestinatarioOpcao[] = [
    { valor: 'criador', label: 'Usuário que criou a ordem de serviço' },
  ];
  public destinatariosPorPerfil: DestinatarioOpcao[] = [];
  public destinatariosPorUsuario: DestinatarioOpcao[] = [];
  public buscaDestinatario = '';

  private valorSalvo: Record<string, unknown> = {};

  public linhas: LinhaAutomacao[] = [
    {
      ativoCtrl: 'isAtivoEmissaoNFe',
      gatilhoCtrl: 'gatilhoEmissaoNFe',
      nome: 'Emitir NF-e',
      icone: 'receipt_long',
      descricao:
        'Emite a nota fiscal de produto (NF-e) dos produtos da ordem, sem intervenção manual. ' +
        'Só roda nas ordens marcadas para emitir NF-e.',
      nota: NOTA_INTERMEDIARIA,
      isEtapaOferecida: e => !e.isInicial && !e.isFinal,
      opcoesCatalogo: res => res.opcoesEmissaoNFe,
      opcoes: [],
    },
    {
      ativoCtrl: 'isAtivoEmissaoNFSe',
      gatilhoCtrl: 'gatilhoEmissaoNFSe',
      nome: 'Emitir NFS-e',
      icone: 'description',
      descricao:
        'Emite a nota fiscal de serviço (NFS-e) da mão de obra da ordem. Só roda nas ordens marcadas para emitir NFS-e.',
      nota: NOTA_INTERMEDIARIA,
      isEtapaOferecida: e => !e.isInicial && !e.isFinal,
      opcoesCatalogo: res => res.opcoesEmissaoNFSe,
      opcoes: [],
    },
    {
      ativoCtrl: 'isAtivoFinanceiro',
      gatilhoCtrl: 'gatilhoFinanceiro',
      nome: 'Gerar financeiro',
      icone: 'payments',
      descricao:
        'Lança as contas a receber da ordem de serviço no Financeiro, conforme a forma de pagamento informada.',
      nota: 'Oferece a transmissão da NFS-e ou qualquer etapa depois da inicial.',
      isEtapaOferecida: e => !e.isInicial,
      opcoesCatalogo: res => res.opcoesFinanceiro,
      opcoes: [],
    },
    {
      ativoCtrl: 'isAtivoMovimentacaoEstoque',
      gatilhoCtrl: 'gatilhoMovimentacaoEstoque',
      nome: 'Movimentar estoque',
      icone: 'inventory_2',
      descricao: 'Lança a saída dos produtos da ordem de serviço no estoque.',
      nota: 'Oferece a autorização da NF-e ou a entrada na etapa inicial.',
      isEtapaOferecida: e => e.isInicial,
      opcoesCatalogo: res => res.opcoesMovimentacaoEstoque,
      opcoes: [],
    },
    {
      ativoCtrl: 'isAtivoReservaEstoque',
      gatilhoCtrl: 'gatilhoReservaEstoque',
      nome: 'Reservar estoque',
      icone: 'bookmark_added',
      descricao:
        'Reserva os produtos da ordem no depósito configurado, para que não sejam vendidos enquanto o serviço ' +
        'não é concluído.',
      nota: NOTA_INTERMEDIARIA,
      isEtapaOferecida: e => !e.isInicial && !e.isFinal,
      opcoesCatalogo: res => res.opcoesReservaEstoque,
      opcoes: [],
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private automacaoOrdemServicoService: AutomacaoOrdemServicoService,
    private configKanbanService: ConfigKanbanService,
    private perfilService: PerfilService,
    private usuarioService: UsuarioService,
    private alertService: AlertService,
    private maskService: MaskService
  ) {
    this.integerMask = { ...this.maskService.getIntegerMask(), allowNegative: false };
  }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      isAtivoEmissaoNFe: [false],
      gatilhoEmissaoNFe: [null],
      isAtivoEmissaoNFSe: [false],
      gatilhoEmissaoNFSe: [null],
      isAtivoFinanceiro: [false],
      gatilhoFinanceiro: [null],
      isAtivoMovimentacaoEstoque: [false],
      gatilhoMovimentacaoEstoque: [null],
      isAtivoReservaEstoque: [false],
      gatilhoReservaEstoque: [null],
      isAtivoLiberacaoReserva: [false],
      idModoAoVencerAutomacaoOrdemServico: [null],
      diasLiberacaoReserva: [null],
      destinatariosLiberacaoReserva: [[]],
    });

    this.linhas.forEach(linha => this.vincularToggle(linha.ativoCtrl, [linha.gatilhoCtrl]));
    this.vincularToggle('isAtivoLiberacaoReserva', [
      'idModoAoVencerAutomacaoOrdemServico',
      'diasLiberacaoReserva',
      'destinatariosLiberacaoReserva',
    ]);

    this.carregar();
  }

  private vincularToggle(ativoCtrl: string, campos: string[]): void {
    this.model
      .get(ativoCtrl)
      ?.valueChanges.subscribe((isAtivo: boolean) => this.habilitarCampos(campos, isAtivo));
  }

  private habilitarCampos(campos: string[], isAtivo: boolean): void {
    campos.forEach(campo => {
      const ctrl = this.model.get(campo);
      if (isAtivo) ctrl?.enable({ emitEvent: false });
      else ctrl?.disable({ emitEvent: false });
    });
  }

  private carregar(): void {
    this.isLoading = true;
    this.isFalhaConfig = false;
    this.tentouSalvar = false;

    forkJoin({
      automacoes: this.automacaoOrdemServicoService.get().pipe(catchError(() => of(null))),
      kanban: this.configKanbanService.get().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
      usuarios: this.usuarioService.getAll().pipe(catchError(() => of(null))),
    }).subscribe(({ automacoes, kanban, perfis, usuarios }) => {
      this.isLoading = false;

      if (perfis && !perfis.isError) {
        this.destinatariosPorPerfil = (perfis.listaPerfil || []).map(item => ({
          valor: `perfil:${item.perfil.idPerfil}`,
          label: item.perfil.nome,
        }));
      }

      if (usuarios && !usuarios.isError) {
        this.destinatariosPorUsuario = (usuarios.listaUsuario || []).map(u => ({
          valor: `usuario:${u.idUsuario}`,
          label: u.nomeUsuario,
        }));
      }

      if (!automacoes || automacoes.isError || !kanban || kanban.isError) {
        this.isFalhaConfig = true;
        this.alertService.show(
          'Aviso',
          automacoes?.errorDescription ||
            kanban?.errorDescription ||
            'Não foi possível carregar as automações. Tente novamente em alguns instantes.',
          AlertType.warning
        );
        return;
      }

      this.aplicarConfiguracao(automacoes, kanban.etapas || []);
    });
  }

  private aplicarConfiguracao(res: GetAutomacaoOrdemServicoResponse, etapas: EtapaKanbanStruct[]): void {
    this.opcoesAoVencer = res.modoAoVencer;

    const etapasGatilho = etapas
      .filter(e => e.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal && e.isAtivo)
      .sort((a, b) => a.ordem - b.ordem);

    this.linhas.forEach(linha => {
      linha.opcoes = [
        ...linha
          .opcoesCatalogo(res)
          .filter(o => o.idOpcaoAutomacao !== OpcaoGatilhoAutomacaoOrdemServicoEnum.TransicaoStatus)
          .map(o => ({ idOpcao: o.idOpcaoAutomacao, idEtapaKanban: null, rotulo: o.rotulo })),
        ...etapasGatilho.filter(linha.isEtapaOferecida).map(e => ({
          idOpcao: OpcaoGatilhoAutomacaoOrdemServicoEnum.TransicaoStatus,
          idEtapaKanban: e.idEtapaKanban,
          rotulo: `Ao mover a ordem para ${e.nome}`,
        })),
      ];
    });

    const config = res.config;
    this.model.reset(
      {
        isAtivoEmissaoNFe: config?.isAtivoEmissaoNFe ?? false,
        gatilhoEmissaoNFe: this.criarGatilho(
          config?.idAutomacaoOrdemServicoOpcaoEmissaoNFe,
          config?.idEtapaKanbanEmissaoNFe
        ),
        isAtivoEmissaoNFSe: config?.isAtivoEmissaoNFSe ?? false,
        gatilhoEmissaoNFSe: this.criarGatilho(
          config?.idAutomacaoOrdemServicoOpcaoEmissaoNFSe,
          config?.idEtapaKanbanEmissaoNFSe
        ),
        isAtivoFinanceiro: config?.isAtivoFinanceiro ?? false,
        gatilhoFinanceiro: this.criarGatilho(
          config?.idAutomacaoOrdemServicoOpcaoFinanceiro,
          config?.idEtapaKanbanFinanceiro
        ),
        isAtivoMovimentacaoEstoque: config?.isAtivoMovimentacaoEstoque ?? false,
        gatilhoMovimentacaoEstoque: this.criarGatilho(
          config?.idAutomacaoOrdemServicoOpcaoMovimentacaoEstoque,
          config?.idEtapaKanbanMovimentacaoEstoque
        ),
        isAtivoReservaEstoque: config?.isAtivoReservaEstoque ?? false,
        gatilhoReservaEstoque: this.criarGatilho(
          config?.idAutomacaoOrdemServicoOpcaoReservaEstoque,
          config?.idEtapaKanbanReservaEstoque
        ),
        isAtivoLiberacaoReserva: config?.isAtivoLiberacaoReserva ?? false,
        idModoAoVencerAutomacaoOrdemServico: config?.idModoAoVencerAutomacaoOrdemServico ?? null,
        diasLiberacaoReserva: config?.diasLiberacaoReserva ?? null,
        destinatariosLiberacaoReserva: (res.destinatarios || []).map(d => this.chaveDestinatario(d)),
      },
      { emitEvent: false }
    );

    this.linhas.forEach(linha =>
      this.habilitarCampos([linha.gatilhoCtrl], this.model.get(linha.ativoCtrl)?.value)
    );
    this.habilitarCampos(
      ['idModoAoVencerAutomacaoOrdemServico', 'diasLiberacaoReserva', 'destinatariosLiberacaoReserva'],
      this.model.get('isAtivoLiberacaoReserva')?.value
    );

    this.valorSalvo = this.model.getRawValue();
  }

  private criarGatilho(idOpcao?: number | null, idEtapaKanban?: number | null): Gatilho | null {
    if (idOpcao == null) return null;
    return { idOpcao, idEtapaKanban: idEtapaKanban ?? null };
  }

  private chaveDestinatario(d: AutomacaoOrdemServicoDestinatarioStruct): string {
    if (d.idTipoDestinatarioAutomacaoOrdemServico === TipoDestinatarioAutomacaoOrdemServicoEnum.Perfil) {
      return `perfil:${d.idPerfil}`;
    }
    if (d.idTipoDestinatarioAutomacaoOrdemServico === TipoDestinatarioAutomacaoOrdemServicoEnum.Usuario) {
      return `usuario:${d.idUsuario}`;
    }
    return 'criador';
  }

  public compararGatilhos(a: Gatilho | null, b: Gatilho | null): boolean {
    if (!a || !b) return false;
    return a.idOpcao === b.idOpcao && a.idEtapaKanban === b.idEtapaKanban;
  }

  public isAtiva(linha: LinhaAutomacao): boolean {
    return !!this.model.get(linha.ativoCtrl)?.value;
  }

  public isGatilhoValido(linha: LinhaAutomacao): boolean {
    const gatilho: Gatilho | null = this.model.get(linha.gatilhoCtrl)?.value;
    return !!gatilho && linha.opcoes.some(o => this.compararGatilhos(o, gatilho));
  }

  public isOrfao(linha: LinhaAutomacao): boolean {
    return !!this.model.get(linha.gatilhoCtrl)?.value && !this.isGatilhoValido(linha);
  }

  public isPendente(linha: LinhaAutomacao): boolean {
    return this.isAtiva(linha) && !this.isGatilhoValido(linha);
  }

  public get isLiberacaoAtiva(): boolean {
    return !!this.model.get('isAtivoLiberacaoReserva')?.value;
  }

  public get isNotificar(): boolean {
    return (
      this.model.get('idModoAoVencerAutomacaoOrdemServico')?.value ===
      ModoAoVencerAutomacaoOrdemServicoEnum.ApenasNotificar
    );
  }

  public get isModoAoVencerEscolhido(): boolean {
    return this.model.get('idModoAoVencerAutomacaoOrdemServico')?.value != null;
  }

  public get isSemDestinatario(): boolean {
    return !this.model.get('destinatariosLiberacaoReserva')?.value?.length;
  }

  public get isLiberacaoPendente(): boolean {
    if (!this.isLiberacaoAtiva) return false;
    const dias = this.model.get('diasLiberacaoReserva')?.value;
    return (
      this.model.get('idModoAoVencerAutomacaoOrdemServico')?.value == null ||
      dias == null ||
      dias <= 0 ||
      (this.isNotificar && this.isSemDestinatario)
    );
  }

  public get textoPendenciaLiberacao(): string {
    return this.isNotificar && this.isSemDestinatario
      ? 'Escolha quem recebe a notificação para manter esta automação ativa.'
      : 'Defina o prazo e a ação ao vencer para manter esta automação ativa.';
  }

  public get notaLiberacao(): string {
    return this.isNotificar
      ? 'Apenas notificar não libera a reserva: o aviso vai por mensagem do sistema e e-mail, e a liberação ' +
          'passa a ser manual, pela ação liberar reserva na ordem.'
      : 'Executar automaticamente libera a reserva de estoque e registra a ocorrência na ordem, sem notificar ninguém.';
  }

  private get quantidadePendentes(): number {
    return this.linhas.filter(l => this.isPendente(l)).length + (this.isLiberacaoPendente ? 1 : 0);
  }

  public get contadorAtivas(): string {
    const ativas =
      this.linhas.filter(l => this.isAtiva(l) && this.isGatilhoValido(l)).length +
      (this.isLiberacaoAtiva && !this.isLiberacaoPendente ? 1 : 0);
    return `${ativas} de ${TOTAL_AUTOMACOES} ativas`;
  }

  public get estadoRodape(): string {
    const valor = this.model.getRawValue();
    const grupos = [
      ...this.linhas.map(l => [l.ativoCtrl, l.gatilhoCtrl]),
      [
        'isAtivoLiberacaoReserva',
        'idModoAoVencerAutomacaoOrdemServico',
        'diasLiberacaoReserva',
        'destinatariosLiberacaoReserva',
      ],
    ];
    const alteradas = grupos.filter(campos =>
      campos.some(c => JSON.stringify(valor[c]) !== JSON.stringify(this.valorSalvo[c]))
    ).length;
    if (!alteradas) return 'Tudo salvo';
    return alteradas === 1 ? '1 alteração não salva' : `${alteradas} alterações não salvas`;
  }

  public labelDestinatario(chave: string): string {
    const todos = [
      ...this.destinatariosDoCard,
      ...this.destinatariosPorPerfil,
      ...this.destinatariosPorUsuario,
    ];
    const conhecido = todos.find(d => d.valor === chave);
    if (conhecido) return conhecido.label;
    return chave.startsWith('perfil:') ? 'Perfil não encontrado' : 'Usuário não encontrado';
  }

  public removerDestinatario(chave: string, evento: Event): void {
    evento.stopPropagation();
    const ctrl = this.model.get('destinatariosLiberacaoReserva');
    ctrl?.setValue((ctrl.value as string[]).filter(c => c !== chave));
    ctrl?.markAsDirty();
  }

  public filtrarDestinatarios(grupo: DestinatarioOpcao[]): DestinatarioOpcao[] {
    const termo = this.buscaDestinatario.trim().toLowerCase();
    if (!termo) return grupo;
    const selecionados: string[] = this.model.get('destinatariosLiberacaoReserva')?.value || [];
    return grupo.filter(d => d.label.toLowerCase().includes(termo) || selecionados.includes(d.valor));
  }

  public alternarComoFunciona(): void {
    this.mostrarComoFunciona = !this.mostrarComoFunciona;
  }

  public descartar(): void {
    this.carregar();
  }

  private montarRequest(): AutomacaoOrdemServicoSalvarRequest {
    const v = this.model.getRawValue();
    const destinatarios: string[] = v.destinatariosLiberacaoReserva || [];

    return {
      isAtivoEmissaoNFe: v.isAtivoEmissaoNFe,
      idAutomacaoOrdemServicoOpcaoEmissaoNFe: v.isAtivoEmissaoNFe
        ? (v.gatilhoEmissaoNFe?.idOpcao ?? null)
        : null,
      idEtapaKanbanEmissaoNFe: v.isAtivoEmissaoNFe ? (v.gatilhoEmissaoNFe?.idEtapaKanban ?? null) : null,
      isAtivoEmissaoNFSe: v.isAtivoEmissaoNFSe,
      idAutomacaoOrdemServicoOpcaoEmissaoNFSe: v.isAtivoEmissaoNFSe
        ? (v.gatilhoEmissaoNFSe?.idOpcao ?? null)
        : null,
      idEtapaKanbanEmissaoNFSe: v.isAtivoEmissaoNFSe ? (v.gatilhoEmissaoNFSe?.idEtapaKanban ?? null) : null,
      isAtivoFinanceiro: v.isAtivoFinanceiro,
      idAutomacaoOrdemServicoOpcaoFinanceiro: v.isAtivoFinanceiro
        ? (v.gatilhoFinanceiro?.idOpcao ?? null)
        : null,
      idEtapaKanbanFinanceiro: v.isAtivoFinanceiro ? (v.gatilhoFinanceiro?.idEtapaKanban ?? null) : null,
      isAtivoMovimentacaoEstoque: v.isAtivoMovimentacaoEstoque,
      idAutomacaoOrdemServicoOpcaoMovimentacaoEstoque: v.isAtivoMovimentacaoEstoque
        ? (v.gatilhoMovimentacaoEstoque?.idOpcao ?? null)
        : null,
      idEtapaKanbanMovimentacaoEstoque: v.isAtivoMovimentacaoEstoque
        ? (v.gatilhoMovimentacaoEstoque?.idEtapaKanban ?? null)
        : null,
      isAtivoReservaEstoque: v.isAtivoReservaEstoque,
      idAutomacaoOrdemServicoOpcaoReservaEstoque: v.isAtivoReservaEstoque
        ? (v.gatilhoReservaEstoque?.idOpcao ?? null)
        : null,
      idEtapaKanbanReservaEstoque: v.isAtivoReservaEstoque
        ? (v.gatilhoReservaEstoque?.idEtapaKanban ?? null)
        : null,
      isAtivoLiberacaoReserva: v.isAtivoLiberacaoReserva,
      idModoAoVencerAutomacaoOrdemServico: v.idModoAoVencerAutomacaoOrdemServico,
      diasLiberacaoReserva: v.diasLiberacaoReserva,
      destinatarios: destinatarios.map(chave => {
        if (chave.startsWith('perfil:')) {
          return {
            idTipoDestinatarioAutomacaoOrdemServico: TipoDestinatarioAutomacaoOrdemServicoEnum.Perfil,
            idPerfil: Number(chave.slice('perfil:'.length)),
            idUsuario: null,
          };
        }
        if (chave.startsWith('usuario:')) {
          return {
            idTipoDestinatarioAutomacaoOrdemServico: TipoDestinatarioAutomacaoOrdemServicoEnum.Usuario,
            idPerfil: null,
            idUsuario: Number(chave.slice('usuario:'.length)),
          };
        }
        return {
          idTipoDestinatarioAutomacaoOrdemServico:
            TipoDestinatarioAutomacaoOrdemServicoEnum.CriadorOrdemServico,
          idPerfil: null,
          idUsuario: null,
        };
      }),
    };
  }

  public salvar(): void {
    if (this.isFalhaConfig) {
      this.alertService.show(
        'Aviso',
        'Não foi possível carregar as automações. Recarregue a tela antes de salvar.',
        AlertType.warning
      );
      return;
    }

    const pendentes = this.quantidadePendentes;
    if (pendentes) {
      this.tentouSalvar = true;
      this.alertService.show(
        'Aviso',
        pendentes === 1
          ? 'Configuração incompleta - 1 automação ligada sem gatilho definido. Escolha a configuração ou desative.'
          : `Configuração incompleta - ${pendentes} automações ligadas sem gatilho definido. ` +
              'Escolha a configuração ou desative.',
        AlertType.warning
      );
      return;
    }

    this.salvando = true;
    this.automacaoOrdemServicoService.salvar(this.montarRequest()).subscribe({
      next: res => {
        this.salvando = false;
        if (res.isError) {
          this.alertService.show(
            'Aviso',
            res.errorDescription ||
              'Não foi possível salvar as automações agora. Tente novamente em alguns instantes.',
            AlertType.warning
          );
          return;
        }
        this.alertService.show('Sucesso', 'Automações salvas.', AlertType.success);
        this.carregar();
      },
      error: () => {
        this.salvando = false;
        this.alertService.show(
          'Aviso',
          'Não foi possível salvar as automações agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }
}
